// getStartedApi.ts
import axios from "axios";
import { toast } from "react-toastify";
import Cookies from "universal-cookie";
import General from "../../../../general";
import config from "../../../../config";

interface UserAuth {
  userId: string;
  token: string;
}

interface ExternalLoginParams {
  entityId: string;
  apiKey: string;
  uniqueUserId?: string;
  uniqueEmailId?: string;
  userId?: string;
  token?: string;
  entityAuthenticationToken?: string;
}

export const QuestThemeDataExport = {
  accentColor: "",
  theme: "",
  borderRadius: "",
  buttonColor: "",
  images: [],
};

export const BrandThemeExport = {
  accentColor: "",
  background: "",
  borderRadius: "",
  buttonColor: "",
  contentColor: "",
  fontFamily: "",
  logo: "",
  primaryColor: "",
  secondaryColor: "",
  tertiaryColor: "",
  titleColor: "",
}

class GetStartedApis {
  private BACKEND_URL: string;
  private GeneralFunctions: General;
  private cookies: Cookies;

  constructor(apiType: string) {
    this.BACKEND_URL =
      apiType === "STAGING" ? config.BACKEND_URL_STAGING : config.BACKEND_URL;
    this.GeneralFunctions = new General("mixpanel", apiType);
    this.cookies = new Cookies();
  }

  async getUserAuth({
    apiKey,
    entityId,
    uniqueUserId,
    uniqueEmailId,
    userId,
    token,
    entityAuthenticationToken,
  }: ExternalLoginParams): Promise<UserAuth | null> {
    try {
      // Check cookies first
      const externalUserId = this.cookies.get("externalUserId");
      const questUserId = this.cookies.get("questUserId");
      const questUserToken = this.cookies.get("questUserToken");

      // If we have valid cookie data and it matches the uniqueUserId
      if (
        externalUserId &&
        questUserId &&
        questUserToken &&
        (externalUserId === uniqueUserId || externalUserId === uniqueEmailId)
      ) {
        return {
          userId: questUserId,
          token: questUserToken,
        };
      }

      // If uniqueUserId is provided, create new user regardless of userId/token
      if (uniqueUserId) {
        const headers = {
          apiKey,
          ...(token && { token }),
          ...(userId && { userId }),
          ...(!!entityAuthenticationToken && {
            "entity-authentication-token": entityAuthenticationToken,
          }),
        };

        const body = {
          externalUserId: uniqueUserId,
          entityId,
          email: uniqueEmailId || null,
          ignoreMAU: true,
        };

        const response = await axios.post(
          `${this.BACKEND_URL}api/users/external/login`,
          body,
          { headers }
        );

        const { userId: newUserId, token: newToken } = response.data;
        const expires = new Date();
        expires.setHours(expires.getHours() + 12);

        this.cookies.set("externalUserId", uniqueUserId, {
          path: "/",
          expires,
        });
        this.cookies.set("questUserId", newUserId, { path: "/", expires });
        this.cookies.set("questUserToken", newToken, { path: "/", expires });

        return {
          userId: newUserId,
          token: newToken,
        };
      }

      // If no uniqueUserId but we have userId and token, use those
      if (userId && token) {
        return { userId, token };
      }

      return null;
    } catch (error) {
      this.GeneralFunctions.captureSentryException(error);
      return null;
    }
  }

  async fetchCampaignData(
    entityId: string,
    questId: string,
    apiKey: string,
    userAuth: UserAuth,
    variation?: string,
    isV1Api?: boolean
  ) {
    try {
      const params = new URLSearchParams();
      params.set("platform", "REACT");
      if (variation) params.set("variation", variation);

      const headers = {
        apiKey,
        userId: userAuth.userId,
        token: userAuth.token,
      };

      const request = isV1Api ? `${this.BACKEND_URL}api/entities/${entityId}/quests/${questId}?userId=${userAuth.userId}` : `${this.BACKEND_URL}api/v2/entities/${entityId}/campaigns/${questId}?${params.toString()}`;
      const response = await axios.get(request, { headers });

      if (!response.data.success) {
        throw new Error(
          response.data.message || "Failed to fetch campaign data"
        );
      }

      return response.data;
    } catch (error) {
      this.GeneralFunctions.captureSentryException(error);
      throw error;
    }
  }

  async verifyAction(
    entityId: string,
    questId: string,
    apiKey: string,
    userAuth: UserAuth,
    action: any,
    campaignVariationId: string,
    isV1Api?: boolean
  ) {
    try {
      const headers = {
        apiKey,
        userId: userAuth.userId,
        token: userAuth.token,
      };
      let criterias: any;
      if (isV1Api) {
        criterias = Array.isArray(action) ?
          action.map((el: any) => (el?.counter >= el?.threshold && !el.completed && (el.type === "DAPP_METRIC_THRESHOLD" || el.type === "MULTIPLE_METRICS")) ? ({ criteriaId: el.actionId }) : null).filter(Boolean) : (action.counter >= action.threshold && !action.completed && (action.type === "DAPP_METRIC_THRESHOLD" || action.type === "MULTIPLE_METRICS")) ? [{ criteriaId: action.actionId }] : action.type === "LINK_OPEN_READ" ? [{ criteriaId: action.actionId }] : [];
      } else {
        criterias = [action];
      }
      if (criterias.length === 0) return;
      const body = isV1Api ? {
        criterias,
        isAccumulateAndSelf: true,
        userId: userAuth.userId,
      } : {
        actions: criterias,
        campaignVariationId,
      };

      const request = isV1Api ? `${this.BACKEND_URL}api/entities/${entityId}/quests/${questId}/verify-all?userId=${userAuth.userId}&autoClaim=false` : `${this.BACKEND_URL}api/v2/entities/${entityId}/campaigns/${questId}/verify`;
      const response = await axios.post(request, body, { headers });

      if (!response.data.success) {
        toast.error(response.data.error);
        throw new Error(response.data.error);
      }

      return response.data;
    } catch (error) {
      this.GeneralFunctions.captureSentryException(error);
      throw error;
    }
  }

  async claimReward(
    entityId: string,
    questId: string,
    apiKey: string,
    userAuth: UserAuth,
    campaignVariationId: string,
    isV1Api?: boolean
  ) {
    try {
      const headers = {
        apiKey,
        userId: userAuth.userId,
        token: userAuth.token,
      };

      const body = {
        userId: userAuth.userId,
        campaignVariationId,
      };

      const request = isV1Api ? `${this.BACKEND_URL}api/entities/${entityId}/quests/${questId}/claim?userId=${userAuth.userId}` : `${this.BACKEND_URL}api/v2/entities/${entityId}/campaigns/${questId}/claim`;
      const response = await axios.post(request, body, { headers });

      if (response.data.success) {
        this.cookies.set("getStartedScreenComplete", true, { path: "/" });
        return response.data;
      } else {
        toast.error(response.data.error);
        throw new Error(response.data.error);
      }
    } catch (error) {
      this.GeneralFunctions.captureSentryException(error);
      throw error;
    }
  }
}

export default GetStartedApis;
